import React, { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faShieldHalved,
  faWrench,
  faBug,
  faCheckCircle,
  faLightbulb,
  faLayerGroup,
  faLock,
} from "@fortawesome/free-solid-svg-icons";

export default function CaseStudyModal({ project, onClose }) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "auto";
    };
  }, [onClose]);

  if (!project) return null;

  const cs = project.caseStudy || {};

  return (
    <div className="cyber-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="cyber-modal-container"
        onClick={(e) => e.stopPropagation()}
        style={{ padding: "32px 28px" }}
      >
        {/* Header */}
        <div className="d-flex align-items-start justify-content-between pb-3 mb-4" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
          <div>
            <div className="d-flex align-items-center gap-2 mb-2">
              <span className="section-label mb-0" style={{ fontSize: "0.7rem", padding: "3px 10px" }}>
                {project.category}
              </span>
              <span
                className="font-mono"
                style={{
                  fontSize: "0.7rem",
                  color: "#22C55E",
                  background: "rgba(34, 197, 94, 0.1)",
                  padding: "3px 8px",
                  borderRadius: "4px",
                  border: "1px solid rgba(34, 197, 94, 0.3)",
                }}
              >
                CASE STUDY
              </span>
            </div>
            <h2 style={{ fontSize: "1.75rem", fontWeight: 700, margin: 0, color: "var(--text-primary)" }}>
              {project.name}
            </h2>
            <p className="mt-2 mb-0" style={{ color: "var(--text-secondary)", fontSize: "0.95rem" }}>
              {project.shortDescription}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            className="btn"
            style={{
              background: "rgba(255, 255, 255, 0.05)",
              border: "1px solid var(--border-subtle)",
              borderRadius: "8px",
              color: "var(--text-secondary)",
              padding: "6px 12px",
              cursor: "pointer",
            }}
          >
            <FontAwesomeIcon icon={faXmark} style={{ fontSize: "1.1rem" }} />
          </button>
        </div>

        {/* Sanitization Notice */}
        <div
          className="d-flex align-items-center gap-2 p-2 px-3 rounded-2 mb-4"
          style={{
            background: "rgba(56, 189, 248, 0.05)",
            border: "1px solid rgba(56, 189, 248, 0.2)",
            fontSize: "0.8rem",
            color: "var(--accent)",
          }}
        >
          <FontAwesomeIcon icon={faLock} />
          <span>
            Confidential infrastructure identifiers, IPs, and sensitive credentials have been strictly sanitized.
          </span>
        </div>

        {/* Special Architectural Callouts if present */}
        {project.retentionPolicy && (
          <div className="mb-4">
            <h4 className="font-mono text-muted text-uppercase mb-3" style={{ fontSize: "0.8rem", letterSpacing: "0.08em" }}>
              Wazuh Index Retention Architecture
            </h4>
            <div className="row g-3">
              {project.retentionPolicy.map((tier, idx) => (
                <div key={idx} className="col-md-4">
                  <div
                    className="p-3 rounded-2 h-100"
                    style={{
                      background: "var(--bg-surface-elevated)",
                      border: `1px solid ${tier.color}40`,
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <span className="font-mono fw-bold" style={{ color: tier.color, fontSize: "0.9rem" }}>
                        {tier.tier}
                      </span>
                      <span className="badge font-mono" style={{ background: "rgba(255, 255, 255, 0.05)", color: "#fff" }}>
                        {tier.duration}
                      </span>
                    </div>
                    <p className="mb-0" style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>
                      {tier.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {project.stages && (
          <div className="mb-4">
            <h4 className="font-mono text-muted text-uppercase mb-3" style={{ fontSize: "0.8rem", letterSpacing: "0.08em" }}>
              Hardening Lifecycle (Before → Hardening → After)
            </h4>
            <div className="row g-3">
              {project.stages.map((st, idx) => (
                <div key={idx} className="col-md-4">
                  <div
                    className="p-3 rounded-2 h-100"
                    style={{
                      background: "var(--bg-surface-elevated)",
                      border: `1px solid ${st.color}40`,
                    }}
                  >
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                      <span className="font-mono fw-bold" style={{ color: st.color, fontSize: "0.95rem" }}>
                        {st.stage}
                      </span>
                      <span
                        className="badge font-mono text-wrap"
                        style={{
                          background: `${st.color}15`,
                          color: st.color,
                          border: `1px solid ${st.color}35`,
                          fontSize: "0.72rem",
                          padding: "4px 8px",
                          lineHeight: 1.3,
                          whiteSpace: "normal",
                          maxWidth: "100%",
                        }}
                      >
                        {st.status}
                      </span>
                    </div>
                    <p className="mb-0" style={{ fontSize: "0.82rem", color: "var(--text-secondary)", lineHeight: 1.5 }}>
                      {st.details}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {project.workflow && (
          <div className="mb-4">
            <h4 className="font-mono text-muted text-uppercase mb-2" style={{ fontSize: "0.8rem", letterSpacing: "0.08em" }}>
              Methodological Workflow
            </h4>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              {project.workflow.map((step, idx) => (
                <React.Fragment key={idx}>
                  <span
                    className="font-mono"
                    style={{
                      fontSize: "0.78rem",
                      background: "var(--bg-surface-elevated)",
                      border: "1px solid var(--border-subtle)",
                      padding: "4px 10px",
                      borderRadius: "6px",
                      color: "var(--text-primary)",
                    }}
                  >
                    <span style={{ color: "var(--accent)", marginRight: "4px" }}>0{idx + 1}.</span> {step}
                  </span>
                  {idx < project.workflow.length - 1 && (
                    <span className="text-muted font-mono" style={{ fontSize: "0.8rem" }}>→</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Detailed Case Study Sections */}
        <div className="row g-4 mt-1">
          {/* Overview & Challenge */}
          <div className="col-md-6">
            <div className="cyber-card-elevated p-3 h-100">
              <div className="d-flex align-items-center gap-2 mb-2" style={{ color: "var(--accent)" }}>
                <FontAwesomeIcon icon={faShieldHalved} />
                <h4 style={{ fontSize: "1rem", fontWeight: 600, margin: 0 }}>Project Overview</h4>
              </div>
              <p style={{ fontSize: "0.88rem", color: "var(--text-secondary)", lineHeight: 1.6 }}>
                {cs.overview || project.shortDescription}
              </p>
            </div>
          </div>

          <div className="col-md-6">
            <div className="cyber-card-elevated p-3 h-100">
              <div className="d-flex align-items-center gap-2 mb-2" style={{ color: "#EF4444" }}>
                <FontAwesomeIcon icon={faBug} />
                <h4 style={{ fontSize: "1rem", fontWeight: 600, margin: 0 }}>The Challenge</h4>
              </div>
              <p style={{ fontSize: "0.88rem", color: "var(--text-secondary)", lineHeight: 1.6 }}>
                {cs.challenge || project.problem}
              </p>
            </div>
          </div>

          {/* Approach & Implementation */}
          <div className="col-md-6">
            <div className="cyber-card-elevated p-3 h-100">
              <div className="d-flex align-items-center gap-2 mb-2" style={{ color: "#818CF8" }}>
                <FontAwesomeIcon icon={faLayerGroup} />
                <h4 style={{ fontSize: "1rem", fontWeight: 600, margin: 0 }}>My Approach &amp; Implementation</h4>
              </div>
              <p style={{ fontSize: "0.88rem", color: "var(--text-secondary)", lineHeight: 1.6 }}>
                {cs.approach} {cs.implementation}
              </p>
            </div>
          </div>

          {/* Tools Used */}
          <div className="col-md-6">
            <div className="cyber-card-elevated p-3 h-100">
              <div className="d-flex align-items-center gap-2 mb-2" style={{ color: "#F59E0B" }}>
                <FontAwesomeIcon icon={faWrench} />
                <h4 style={{ fontSize: "1rem", fontWeight: 600, margin: 0 }}>Tools &amp; Technologies</h4>
              </div>
              <div className="d-flex flex-wrap gap-2 pt-1">
                {(project.technologies || []).map((tool, idx) => (
                  <span key={idx} className="cyber-tag font-mono">
                    {tool}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Findings & Remediation */}
          <div className="col-md-6">
            <div className="cyber-card-elevated p-3 h-100">
              <div className="d-flex align-items-center gap-2 mb-2" style={{ color: "#F472B6" }}>
                <FontAwesomeIcon icon={faShieldHalved} />
                <h4 style={{ fontSize: "1rem", fontWeight: 600, margin: 0 }}>Security Findings</h4>
              </div>
              <p style={{ fontSize: "0.88rem", color: "var(--text-secondary)", lineHeight: 1.6 }}>
                {cs.findings}
              </p>
            </div>
          </div>

          <div className="col-md-6">
            <div className="cyber-card-elevated p-3 h-100">
              <div className="d-flex align-items-center gap-2 mb-2" style={{ color: "#22C55E" }}>
                <FontAwesomeIcon icon={faCheckCircle} />
                <h4 style={{ fontSize: "1rem", fontWeight: 600, margin: 0 }}>Remediation &amp; Outcome</h4>
              </div>
              <p style={{ fontSize: "0.88rem", color: "var(--text-secondary)", lineHeight: 1.6 }}>
                {cs.remediation} {cs.outcome}
              </p>
            </div>
          </div>

          {/* Lessons Learned */}
          {cs.lessonsLearned && (
            <div className="col-12">
              <div
                className="p-3 rounded-2"
                style={{
                  background: "rgba(56, 189, 248, 0.04)",
                  border: "1px solid rgba(56, 189, 248, 0.2)",
                }}
              >
                <div className="d-flex align-items-center gap-2 mb-1" style={{ color: "var(--accent)" }}>
                  <FontAwesomeIcon icon={faLightbulb} />
                  <h4 style={{ fontSize: "0.95rem", fontWeight: 600, margin: 0 }}>Lessons Learned &amp; Engineering Insight</h4>
                </div>
                <p className="mb-0" style={{ fontSize: "0.88rem", color: "var(--text-primary)", lineHeight: 1.6 }}>
                  {cs.lessonsLearned}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="d-flex justify-content-end mt-4 pt-3" style={{ borderTop: "1px solid var(--border-subtle)" }}>
          <button type="button" onClick={onClose} className="btn-cyber-secondary">
            Close Case Study
          </button>
        </div>
      </div>
    </div>
  );
}

